import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './InfiniteQuestPanel.css';

const API = process.env.REACT_APP_SERVER_URL || '';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// The endless, branching "Chronicler" questline. Players only ever pick from a
// fixed set of options — never free text — and every choice shapes what comes next.
const InfiniteQuestPanel = ({ character, onClose, onCharacterUpdate, onOpenCrownShop }) => {
  const [loading, setLoading] = useState(true);
  const [entitled, setEntitled] = useState(false);
  const [llmReady, setLlmReady] = useState(true);
  const [state, setState] = useState(null);   // { started, currentQuest|quest, badge, threads, dailyRemaining, ... }
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [lastReward, setLastReward] = useState(null);

  const flash = (text, isError) => {
    setMessage({ text, isError });
    setTimeout(() => setMessage(null), 3500);
  };

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/infinite-quest/status`, authHeaders());
      setEntitled(!!res.data.entitled);
      setLlmReady(res.data.llmReady !== false);
      setState(res.data.state || null);
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to load', true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const quest = state?.quest || state?.currentQuest || null;
  const badge = state?.badge || null;
  const threads = state?.threads || [];

  const applyResult = (data) => {
    setState(data);
    if (data.choiceResult?.rewards) {
      const { gold, xp } = data.choiceResult.rewards;
      setLastReward({ gold, xp, streak: data.choiceResult.streak });
    }
    if (onCharacterUpdate) onCharacterUpdate();
  };

  const handleStart = async (reset = false) => {
    if (busy) return;
    setBusy(true);
    setLastReward(null);
    try {
      const res = await axios.post(`${API}/api/infinite-quest/start`, { reset }, authHeaders());
      applyResult(res.data);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'DAILY_CAP') flash('You have reached your 25-quest daily limit. Come back tomorrow!', true);
      else if (code === 'LLM_DOWN') flash('The Chronicler is resting — quest generation is temporarily unavailable.', true);
      else flash(err.response?.data?.error || 'Failed to begin', true);
    }
    setBusy(false);
  };

  const handleChoose = async (optionId) => {
    if (busy) return;
    setBusy(true);
    setLastReward(null);
    try {
      const res = await axios.post(`${API}/api/infinite-quest/choose`, { optionId }, authHeaders());
      applyResult(res.data);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'DAILY_CAP') flash('You have reached your 25-quest daily limit. Come back tomorrow!', true);
      else if (code === 'LLM_DOWN') flash('The Chronicler is resting — try again shortly.', true);
      else flash(err.response?.data?.error || 'Failed', true);
    }
    setBusy(false);
  };

  const renderBadge = () => {
    if (!badge || !badge.active) return null;
    return (
      <span className="iq-badge" title={`${badge.tierName} — +${badge.discountPct}% Crown Shop discount`}>
        📜 {badge.tierName} · Tier {badge.level}/{badge.maxLevel}
      </span>
    );
  };

  const renderBody = () => {
    if (loading) return <div className="iq-loading">Consulting the Chronicler…</div>;

    if (!entitled) {
      return (
        <div className="iq-upsell">
          <h3>📜 The Chronicler Awaits</h3>
          <p>
            Unlock an endless, personalized saga written for your character. Every choice
            branches the story — and the rewards are real Gold and XP. Build a daily streak
            for a badge on your character sheet and stacking Crown Shop discounts.
          </p>
          <p style={{ color: '#c9a84c' }}>Available with the Chronicler subscription — $7.99/month.</p>
          <button className="iq-cta" onClick={() => { if (onOpenCrownShop) onOpenCrownShop(); onClose(); }}>
            View Chronicler Subscription
          </button>
        </div>
      );
    }

    if (!llmReady) {
      return (
        <div className="iq-empty">
          <p>The Chronicler is resting — quest generation is temporarily unavailable. Please try again soon.</p>
        </div>
      );
    }

    if (!state || !state.started || !quest) {
      return (
        <div className="iq-empty">
          <p>Your saga has not yet begun. The Chronicler stands ready to weave a tale around your deeds.</p>
          <button className="iq-cta" disabled={busy} onClick={() => handleStart(false)}>
            {busy ? 'Beginning…' : 'Begin Your Saga'}
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="iq-meta">
          <span>Quest #{state.questCount}</span>
          <span>{state.dailyRemaining ?? 0} of {state.dailyCap ?? 25} quests left today</span>
        </div>

        {lastReward && (
          <div className="iq-reward">
            ✦ Earned {lastReward.gold} gold and {lastReward.xp} XP
            {lastReward.streak?.leveledUp ? ` — streak advanced to tier ${badge?.level}!` : ''}
          </div>
        )}

        <h3 className="iq-quest-title">{quest.title}</h3>
        <div className="iq-quest-sub">
          {quest.giver ? `Given by ${quest.giver}` : ''}{quest.giver && quest.location ? ' · ' : ''}{quest.location || ''}
        </div>
        <div className="iq-quest-desc">{quest.description}</div>

        <div className="iq-options">
          {(quest.options || []).map(opt => (
            <button
              key={opt.id}
              className="iq-option-btn"
              disabled={busy}
              onClick={() => handleChoose(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {threads.length > 0 && (
          <div className="iq-log">
            <h4>Ongoing Threads</h4>
            {threads.slice(0, 8).map(t => (
              <div key={t.id} className="iq-thread">
                • {t.label} <span className="iq-thread-status">({t.status})</span>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="iq-overlay" onClick={onClose}>
      <div className="iq-modal" onClick={e => e.stopPropagation()}>
        <div className="iq-header">
          <h2>📜 The Endless Saga</h2>
          {renderBadge()}
          <button className="iq-close-btn" onClick={onClose} title="Close (Esc)">✖</button>
        </div>

        {message && (
          <div className={`iq-flash${message.isError ? ' error' : ' success'}`} style={{ margin: '12px 18px 0' }}>
            {message.text}
          </div>
        )}

        <div className="iq-body">{renderBody()}</div>
      </div>
    </div>
  );
};

export default InfiniteQuestPanel;
